import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

import { useGetActivity } from '../../../api/generated/endpoints/admin/admin'
import type { ActivityEntry } from '../../../api/generated/model'
import { ActivityRow } from '../../../components/ActivityList'
import { formatDayLong, localDateOf, todayAtFacility } from '../../../lib/time'

/**
 * Movimientos: everything done in the admin, newest first.
 *
 * For control within the organisation, after the fact: who blocked a bay,
 * who cancelled a class, who changed a price. Filtered by person, grouped by
 * day, each line linked to the booking it changed. Read-only -- nothing here
 * can change what it shows.
 */
export const Route = createFileRoute('/_authed/_admin/admin/activity')({
  validateSearch: (search: Record<string, unknown>): { actor?: string } => ({
    actor:
      typeof search.actor === 'string' && search.actor.startsWith('admin:')
        ? search.actor
        : undefined,
  }),
  component: ActivityPage,
})

function ActivityPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  // Each "Ver más" adds a page keyed by where the previous one stopped, so a
  // change made while he reads never shows twice.
  const [cursors, setCursors] = useState<(number | undefined)[]>([undefined])
  const first = useGetActivity({ actor: search.actor })
  const people = first.data?.people ?? []

  const choose = (actor: string | undefined) => {
    setCursors([undefined])
    void navigate({ search: { actor }, replace: true })
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-3xl font-semibold">Movimientos</h1>
        <p className="mt-2 text-slate-700">
          Todo lo que se hizo desde el panel, lo último primero: quién lo hizo y cuándo.
        </p>
      </div>

      {people.length > 1 && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrar por persona">
          {[{ actor: undefined, label: 'Todos' }, ...people].map((person) => {
            const current = search.actor === person.actor
            return (
              <button
                key={person.actor ?? 'all'}
                type="button"
                aria-pressed={current}
                onClick={() => choose(person.actor)}
                className={[
                  'min-h-11 rounded-full border px-4 text-base focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900',
                  current
                    ? 'border-slate-900 bg-slate-900 text-white'
                    : 'border-slate-300 bg-white hover:border-slate-500',
                ].join(' ')}
              >
                <span className="[overflow-wrap:anywhere]">{person.label}</span>
              </button>
            )
          })}
        </div>
      )}

      {first.isPending && <p className="text-slate-600">Cargando…</p>}
      {first.isError && (
        <p role="alert" className="text-red-700">
          No se pudieron cargar los movimientos.
        </p>
      )}
      {first.data && first.data.entries.length === 0 && (
        <p className="rounded-lg border border-slate-300 bg-white p-5 text-slate-700">
          Todavía no hay movimientos{search.actor ? ' de esa persona' : ''}.
        </p>
      )}

      {cursors.map((cursor, index) => (
        <Page
          key={`${search.actor ?? 'all'}:${cursor ?? 'first'}`}
          actor={search.actor}
          beforeId={cursor}
          isLast={index === cursors.length - 1}
          onMore={(next) => setCursors([...cursors, next])}
        />
      ))}
    </div>
  )
}

function Page({
  actor,
  beforeId,
  isLast,
  onMore,
}: {
  actor: string | undefined
  beforeId: number | undefined
  isLast: boolean
  onMore: (next: number) => void
}) {
  const page = useGetActivity({ actor, before_id: beforeId })
  if (!page.data) return null
  const next = page.data.next_before_id

  return (
    <>
      {groupByDay(page.data.entries).map(([day, entries]) => (
        <section key={`${beforeId ?? 'first'}:${day}`}>
          <h2 className="text-lg font-semibold text-slate-700">
            {day === todayAtFacility() ? 'Hoy' : formatDayLong(day)}
          </h2>
          <ul className="mt-2 divide-y divide-slate-200 rounded-lg border border-slate-300 bg-white px-5">
            {entries.map((entry) => (
              <ActivityRow key={entry.id} entry={entry} showBooking clockOnly />
            ))}
          </ul>
        </section>
      ))}
      {isLast && next !== null && next !== undefined && (
        <button
          type="button"
          onClick={() => onMore(next)}
          className="min-h-12 w-full rounded-md border-2 border-slate-300 bg-white text-lg hover:border-slate-500 focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
        >
          Ver más
        </button>
      )}
    </>
  )
}

/** [day, entries] in the order given, by the facility's calendar day. */
function groupByDay(entries: ActivityEntry[]): Array<[string, ActivityEntry[]]> {
  const groups = new Map<string, ActivityEntry[]>()
  for (const entry of entries) {
    const day = localDateOf(entry.at)
    const list = groups.get(day)
    if (list) list.push(entry)
    else groups.set(day, [entry])
  }
  return [...groups.entries()]
}
